/**
 * Soften — Free Gift With Purchase
 *
 * Keeps a bonus product in step with the cart: while one of the configured
 * trigger products is in the cart, the gift line rides along with it, and the
 * gift is dropped again as soon as the trigger leaves. The price reduction is
 * NOT applied here — that comes from a Shopify "Buy X Get Y" automatic
 * discount set up in admin. This module owns nothing but the add and remove
 * of the gift line.
 *
 * Sync rules:
 *   - Runs once at page load, then again after every cart-lines update.
 *   - Trigger in cart, no gift yet, shopper has not declined → add the gift.
 *   - Trigger gone → drop the gift and forget any earlier decline.
 *   - Shopper deletes the gift while the trigger is still there → read that as
 *     a decline and leave it out for the rest of the browser session.
 *
 * MERCHANT SETUP — this module does nothing until all three are done:
 *   1. Point `triggerProductHandles` and `giftProductHandle` (see CONFIG
 *      below) at real product handles from this store.
 *   2. Turn inventory tracking off on the gift product, or keep it well
 *      stocked — the add call fails once available stock hits zero.
 *   3. Create the matching "Buy X Get Y → 100% off" automatic discount in
 *      Shopify admin, otherwise the gift is charged at its full price.
 *
 * The gift line carries an underscore-prefixed line-item property, which
 * Shopify treats as private — the shopper never sees it in the cart, at
 * checkout, or in notification emails.
 */

import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const CONFIG = {
  // REQUIRED — product handles that unlock the gift. Every variant of each
  // handle counts as a trigger. Replace the placeholder before going live.
  triggerProductHandles: ['REPLACE-WITH-TRIGGER-PRODUCT-HANDLE'],
  // REQUIRED — handle of the product given away. Its FIRST variant is what
  // gets added, so a single-variant gift product is the safe choice.
  // Replace the placeholder before going live.
  giftProductHandle: 'REPLACE-WITH-GIFT-PRODUCT-HANDLE',
  // sessionStorage key remembering that this shopper removed the gift.
  removalFlagKey: 'soften-free-gift-removed',
  // Private (underscore-prefixed) line-item property tagged onto the gift.
  giftLineProperty: '_free_gift',
};

let cachedGiftVariantId = null;
// The lookup gets its own boolean instead of leaning on cachedGiftVariantId.
// A gift product that cannot be found caches as null, which is falsy, so a
// plain truthiness check would read "looked it up, it isn't there" as "never
// looked it up" and fire the same failing request on every page load, every
// cart update and every add-to-cart. Those wasted storefront hits count
// against the shop's rate limit, so one miss has to be enough.
let giftLookupDone = false;
// Same reasoning as giftLookupDone above, applied to the trigger side. An empty
// Set is truthy but `.size` is 0, so guarding on size alone reads "looked it up,
// found nothing" as "never looked it up" and refetches a missing handle on every
// sync. The flag records that the lookup ran, whatever it returned.
let triggerLookupDone = false;
const cachedTriggerVariantIds = new Set();
let isMutating = false;
let lastSeenGift = false;

async function fetchVariantsForHandle(handle) {
  try {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) return [];
    const data = await res.json();
    return (data.variants || []).map((v) => v.id);
  } catch {
    return [];
  }
}

async function getGiftVariantId() {
  if (giftLookupDone) return cachedGiftVariantId;
  const variants = await fetchVariantsForHandle(CONFIG.giftProductHandle);
  cachedGiftVariantId = variants[0] || null;
  giftLookupDone = true;
  if (!cachedGiftVariantId) {
    console.warn(
      `[soften-free-gift] gift product "${CONFIG.giftProductHandle}" not found — feature disabled for this page load`
    );
  }
  return cachedGiftVariantId;
}

async function getTriggerVariantIds() {
  if (triggerLookupDone) return cachedTriggerVariantIds;
  triggerLookupDone = true;
  for (const handle of CONFIG.triggerProductHandles) {
    const variants = await fetchVariantsForHandle(handle);
    variants.forEach((id) => cachedTriggerVariantIds.add(id));
  }
  return cachedTriggerVariantIds;
}

async function getCart() {
  const res = await fetch('/cart.js', { credentials: 'same-origin' });
  if (!res.ok) return { items: [] };
  return res.json();
}

function isOptedOut() {
  try {
    return sessionStorage.getItem(CONFIG.removalFlagKey) === '1';
  } catch {
    return false;
  }
}

function markOptedOut() {
  try {
    sessionStorage.setItem(CONFIG.removalFlagKey, '1');
  } catch {
    /* private mode */
  }
}

function clearOptedOut() {
  try {
    sessionStorage.removeItem(CONFIG.removalFlagKey);
  } catch {
    /* private mode */
  }
}

function findGiftLine(cart, giftVariantId) {
  return cart.items.find((it) => it.variant_id === giftVariantId);
}

function hasTriggerInCart(cart, triggerVariantIds) {
  return cart.items.some((it) => triggerVariantIds.has(it.variant_id));
}

async function addGift(giftVariantId) {
  const body = new FormData();
  body.append('items[0][id]', String(giftVariantId));
  body.append('items[0][quantity]', '1');
  body.append(`items[0][properties][${CONFIG.giftLineProperty}]`, 'true');

  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body,
  });
  return res.ok;
}

async function removeLine(line) {
  const body = new FormData();
  body.append('id', String(line.key));
  body.append('quantity', '0');

  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body,
  });
  return res.ok;
}

function notifyCartChanged(action) {
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'soften-free-gift',
    })
  );
}

async function sync() {
  if (isMutating) return;

  const [giftVariantId, triggerVariantIds] = await Promise.all([
    getGiftVariantId(),
    getTriggerVariantIds(),
  ]);
  if (!giftVariantId || triggerVariantIds.size === 0) return;

  const cart = await getCart();
  const triggerPresent = hasTriggerInCart(cart, triggerVariantIds);
  const giftLine = findGiftLine(cart, giftVariantId);

  // The gift has vanished from a cart that still holds the trigger, so the
  // shopper took it out by hand. Record the decline and leave it at that.
  if (lastSeenGift && !giftLine && triggerPresent) {
    markOptedOut();
    lastSeenGift = false;
    return;
  }

  if (!triggerPresent) {
    // Nothing in the cart qualifies any more: sweep up a stranded gift line
    // and wipe the decline flag, so a later qualifying add starts clean.
    clearOptedOut();
    if (giftLine) {
      isMutating = true;
      try {
        await removeLine(giftLine);
        notifyCartChanged('remove');
      } finally {
        isMutating = false;
        lastSeenGift = false;
      }
    }
    return;
  }

  if (giftLine) {
    lastSeenGift = true;
    return;
  }

  if (isOptedOut()) return;

  isMutating = true;
  try {
    const ok = await addGift(giftVariantId);
    if (ok) {
      lastSeenGift = true;
      notifyCartChanged('add');
    }
  } finally {
    isMutating = false;
  }
}

function onCartUpdate() {
  if (isMutating) return;
  sync().catch((err) => console.warn('[soften-free-gift]', err));
}

async function init() {
  const giftVariantId = await getGiftVariantId();
  if (giftVariantId) {
    const cart = await getCart();
    lastSeenGift = !!findGiftLine(cart, giftVariantId);
  }
  await sync().catch((err) => console.warn('[soften-free-gift]', err));
}

// Stay dormant in the theme editor. A cart mutated from inside the preview
// frame belongs to no real shopper, and every extra storefront request the
// preview fires eats into the shop's rate limit — pile up enough of them and
// the editor gets 429s back instead of a rendered page.
const inThemeEditor = Boolean(window.Shopify && window.Shopify.designMode);

if (!inThemeEditor) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener(StandardEvents.cartLinesUpdate, onCartUpdate);
}

/* ------------------------------------------------------------------
 * Public hook for the custom PDP add-to-cart in soften.js
 *
 * That handler takes the form submit over itself: it POSTs to
 * /cart/add.js with bundled section rendering and morphs the returned
 * cart-drawer-section straight into the page. What it never does is
 * dispatch a CartLinesUpdateEvent, so the `sync()` listener registered
 * above stays silent through the very first add — and the gift would be
 * missing from the one render the shopper is actually looking at.
 *
 * This hook closes that gap. soften.js calls it once its own add has
 * succeeded but before it paints the drawer. We fire a second
 * /cart/add.js for the gift, again asking for
 * `sections=cart-drawer-section`, and hand the response back. soften.js
 * renders OUR section markup, which already carries both lines, so the
 * shopper sees one smooth update with the gift arriving alongside the
 * product that earned it.
 * ------------------------------------------------------------------ */

/**
 * @param {Array<string | number>} addedVariantIds - Variant IDs that were just added in the preceding /cart/add.js call.
 * @returns {Promise<object | null>} The /cart/add.js response (with `sections` + `item_count`) if a gift was added, otherwise null.
 */
async function maybeAddGiftAfter(addedVariantIds) {
  if (isMutating) return null;

  const [giftVariantId, triggerVariantIds] = await Promise.all([
    getGiftVariantId(),
    getTriggerVariantIds(),
  ]);
  if (!giftVariantId || triggerVariantIds.size === 0) return null;

  const idsAsNumbers = (addedVariantIds || []).map((id) => Number(id));
  const addedTrigger = idsAsNumbers.some((id) => triggerVariantIds.has(id));
  if (!addedTrigger) return null;

  const cart = await getCart();
  if (findGiftLine(cart, giftVariantId)) {
    lastSeenGift = true;
    return null;
  }
  if (isOptedOut()) return null;

  isMutating = true;
  try {
    const body = new FormData();
    body.append('items[0][id]', String(giftVariantId));
    body.append('items[0][quantity]', '1');
    body.append(`items[0][properties][${CONFIG.giftLineProperty}]`, 'true');
    body.append('sections', 'cart-drawer-section');

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    if (!res.ok) return null;
    const data = await res.json();
    lastSeenGift = true;
    return data;
  } catch (err) {
    console.warn('[soften-free-gift] maybeAddGiftAfter failed', err);
    return null;
  } finally {
    isMutating = false;
  }
}

window.softenFreeGift = Object.assign(window.softenFreeGift || {}, {
  maybeAddGiftAfter,
});
